import React, { FunctionComponent } from 'react';

import Linkify from 'linkifyjs/react';
import { groupBy } from 'lodash';
import { Button, Image } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ReactPlayer from 'react-player/lazy';

import { ActionId } from 'admin/actions/Actions';
import { isAuthorized } from 'auth/Auth';
import { Comment, MediaType, ApprovalStatus, MediaMimeType } from 'common/comments2/Comments2.types';
import { LinkMetadata } from 'common/link-metadata/LinkMetadata';

import 'common/comments2/message/Message.scss';

interface Props {
    comment2: Comment;
}

interface MessageProps extends Props {
    approveHandler?: (approved: boolean, comment: Comment) => void;
}

const TextMessage: FunctionComponent<Props> = ({ comment2 }) => {
    return (
        <div className="r4e-message text">
            <Linkify>
                {/* eslint-disable-next-line react/no-danger */}
                <span dangerouslySetInnerHTML={{ __html: comment2.text! }} />
            </Linkify>
        </div>
    );
};

const MediaMessage: FunctionComponent<Props> = ({ comment2 }) => {
    const mediasByType = groupBy(comment2.medias, 'mediaType');

    return (
        <div className="r4e-message media">
            {Object.keys(mediasByType).map((type) => {
                const medias = mediasByType[type];

                return (
                    <div className="media" key={type}>
                        {medias.map((media) => {
                            switch (media.mediaType) {
                                case MediaType.Link:
                                    if (media.mimeType === MediaMimeType.Pdf) {
                                        return (
                                            <div className="media-wrapper pdf" key={media.url}>
                                                <iframe
                                                    title="pdfloader"
                                                    src={media.url}
                                                    width="500px"
                                                    height="300px"
                                                />
                                            </div>
                                        );
                                    }

                                    return (
                                        <div className="media-wrapper link" key={media.url}>
                                            <Linkify>{media.url}</Linkify>
                                        </div>
                                    );
                                case MediaType.Photo:
                                case MediaType.Sticker:
                                    return (
                                        <a
                                            className="media-wrapper photo"
                                            href={media.url}
                                            key={media.url}
                                            target="_blank"
                                            aria-label="message-media"
                                            rel="noopener noreferrer"
                                        >
                                            {/* Check if we have alt tags for images */}
                                            <Image src={media.thumbnailUrl || media.url} fluid alt={media?.title} />
                                        </a>
                                    );

                                case MediaType.Video:
                                    return (
                                        <div className="media-wrapper video" key={media.url}>
                                            <ReactPlayer controls width="100%" height="100%" url={media.url} />
                                        </div>
                                    );
                                default:
                                    // This was handling type of 'Not_Mapped' which included expanding fb urls for external links
                                    /* <span className="media-wrapper" key={media.url}>
                                            <Linkify>{media.url}</Linkify>
                                        </span> */
                                    return null;
                            }
                        })}
                    </div>
                );
            })}
        </div>
    );
};

export const Message: FunctionComponent<MessageProps> = ({ comment2, approveHandler }) => {
    const { t } = useTranslation('common/comments2');
    const isPending = [ApprovalStatus.PendingApproval, ApprovalStatus.PendingModeration].includes(
        comment2.approver?.approvalStatus!,
    );
    const isRejected = ApprovalStatus.Rejected === comment2.approver?.approvalStatus;

    return (
        <div className="r4e-message">
            <div className="r4e-message-content">
                {comment2.text ? (
                    <div className="r4e-message-text">
                        <TextMessage comment2={comment2} />
                        <div className="r4e-link-metadata-container">
                            <LinkMetadata tenantId={comment2.tenantID} text={comment2.text} />
                        </div>
                    </div>
                ) : null}
                {isPending ? (
                    <div className="pending">
                        <i className="icon-hourglass" />
                        {ApprovalStatus.PendingApproval === comment2.approver?.approvalStatus
                            ? t('PENDING_APPROVAL')
                            : t('PENDING_MODERATION')}
                    </div>
                ) : null}
                {isRejected ? (
                    <div className="rejected">
                        <i className="icon-exclamation-circle" />
                        {t('REJECTED')}
                    </div>
                ) : null}
            </div>
            {(comment2.medias?.length || 0) > 0 ? <MediaMessage comment2={comment2} /> : null}
            {isPending &&
                (isAuthorized({ actions: [ActionId.MessagingInboxResponses.Approve] }) ? (
                    <div className="approval-actions">
                        <Button variant="danger" onClick={(): void | undefined => approveHandler?.(false, comment2)}>
                            {t('REJECT')}
                        </Button>
                        <Button variant="primary" onClick={(): void | undefined => approveHandler?.(true, comment2)}>
                            {t('APPROVE')}
                        </Button>
                    </div>
                ) : null)}
        </div>
    );
};
